/**
 * Журнал заказов.
 *
 * Отчёт по выручке отвечает на вопрос «сколько заработали». Этот —
 * «что происходило»: здесь и закрытые чеки, и отменённые, и те, что
 * до сих пор в работе.
 *
 * Незакрытый стол с вечера в выручке не виден вовсе — а это чаще
 * всего забытый заказ, который держит место и путает зал. Владелец
 * должен находить такое сам, не дожидаясь жалобы официанта.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@dastarhan/core';

const API = '/api/v1';

type Row = {
  id: string;
  kind: 'order' | 'open';
  number: number;
  status: 'closed' | 'cancelled' | 'working' | 'awaiting';
  tableName: string | null;
  openedAt: string;
  closedAt: string | null;
  total: number;
  comment: string | null;
  waiter: string | null;
  payments: { method: string; amount: number; at: string }[];
};

const METHOD_RU: Record<string, string> = {
  CASH: 'Наличными',
  CARD: 'Безналичный',
  BONUS: 'Бонусами',
  DEPOSIT: 'Депозит брони',
};

const STATUS: Record<Row['status'], { label: string; cls: string }> = {
  closed: { label: 'Закрыт', cls: 'st-closed' },
  cancelled: { label: 'Отменён', cls: 'st-cancelled' },
  working: { label: 'В работе', cls: 'st-working' },
  awaiting: { label: 'Ждёт оплаты', cls: 'st-awaiting' },
};

const hhmm = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });

export function OrdersLogScreen({ token, locationId }: {
  token: string;
  locationId?: string;
}) {
  const [openId, setOpenId] = useState<string | null>(null);

  /**
   * Окно дат. По умолчанию сегодня: владелец чаще смотрит текущий день,
   * а за прошлым приходит с конкретным вопросом и знает дату.
   */
  const today = new Date().toLocaleDateString('sv-SE');   // ГГГГ-ММ-ДД
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const h = { Authorization: `Bearer ${token}` };

  const log = useQuery({
    queryKey: ['orders-log', locationId, from, to],
    queryFn: async () => {
      const p = new URLSearchParams({ from, to });
      if (locationId) p.set('locationId', locationId);
      const q = `?${p.toString()}`;
      const r = await fetch(`${API}/reports/orders-log${q}`, { headers: h });
      return r.json() as Promise<{
        rows: Row[];
        shift: { openedAt: string; closedAt: string | null } | null;
        totals: { count: number; revenue: number; working: number };
      }>;
    },
    refetchInterval: 30_000,
  });

  /* Выбор дней: быстрые кнопки для частого и поля для точного.
     «Что было в субботу» — обычный вопрос владельца, и ради него
     не должно приходиться листать сотню строк */
  const dates = (
    <>
      <div className="ol-quick">
        <button className={`btn small ${from === today && to === today ? 'accent' : ''}`}
          onClick={() => { setFrom(today); setTo(today); }}>Сегодня</button>
        <button className="btn small" onClick={() => {
          const d = new Date(); d.setDate(d.getDate() - 1);
          const y = d.toLocaleDateString('sv-SE');
          setFrom(y); setTo(y);
        }}>Вчера</button>
        <button className="btn small" onClick={() => {
          const d = new Date(); d.setDate(d.getDate() - 6);
          setFrom(d.toLocaleDateString('sv-SE')); setTo(today);
        }}>7 дней</button>
        <button className="btn small" onClick={() => {
          const d = new Date(); d.setDate(d.getDate() - 29);
          setFrom(d.toLocaleDateString('sv-SE')); setTo(today);
        }}>30 дней</button>
      </div>
      <label className="ol-range">
        с <input type="date" value={from} max={to}
          onChange={(e) => setFrom(e.target.value)} />
        по <input type="date" value={to} min={from} max={today}
          onChange={(e) => setTo(e.target.value)} />
      </label>
    </>
  );

  if (log.isPending) return <p className="hint">Загружаем…</p>;

  // Сутки — граница, после которой открытый стол почти наверняка забыт:
  // ни одна компания не сидит дольше, а смена давно закончилась
  const dayAgo = Date.now() - 24 * 3600_000;
  const stale = (log.data?.rows ?? []).filter(
    (r) => (r.status === 'working' || r.status === 'awaiting')
      && new Date(r.openedAt).getTime() < dayAgo,
  ).length;
  const data = log.data;
  if (!data || data.rows.length === 0) {
    // Пусто — это про выбранные дни, а не про заведение вообще.
    // Без этой оговорки владелец решит, что журнал сломан
    const label = from === to
      ? new Date(`${from}T12:00`).toLocaleDateString('ru-RU',
          { day: 'numeric', month: 'long' })
      : `${new Date(`${from}T12:00`).toLocaleDateString('ru-RU')} — ${new Date(`${to}T12:00`).toLocaleDateString('ru-RU')}`;
    return (
      <section className="orders-log">
        <div className="ol-dates">{dates}</div>
        <p className="note">За {label} заказов не было.</p>
      </section>
    );
  }

  return (
    <section className="orders-log">
      <div className="ol-dates">{dates}</div>

      <header className="ol-head">
        <div>
          <h2>Заказы</h2>
          {/* Незакрытые — первое, что должен увидеть владелец: стол,
              висящий третьи сутки, не найдётся ни в одном отчёте
              по выручке */}
          {data.totals.working > 0 && (
            <span className="ol-warn">
              В работе: {data.totals.working}
            </span>
          )}
          {/* Столы, открытые больше суток назад, — почти всегда забытые.
              Официант ушёл, гости давно разошлись, а стол занят
              и в отчёты не попадает */}
          {stale > 0 && (
            <span className="ol-stale">
              Открыты больше суток: {stale} — проверьте, не забыты ли
            </span>
          )}
        </div>
        <div className="ol-right">
          <button className="btn" onClick={() => window.print()}>
            ⎙ Печать A4
          </button>
          <div className="ol-sum">
            <span>{data.totals.count} заказов</span>
            <b>{formatMoney(data.totals.revenue)}</b>
          </div>
        </div>
      </header>

      <table className="grid orders-table">
        <thead>
          <tr>
            <th>Заказ</th>
            <th>Статус</th>
            <th>Стол</th>
            <th>Официант</th>
            <th>Открыт</th>
            <th>Закрыт</th>
            <th>Оплата</th>
            <th className="num">Сумма</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id} className="ol-row" onClick={() => setOpenId(r.id)}>
              <td className="ol-num">
                {String(r.number).padStart(4, '0')}
                {/* Дата под номером: номера свои в каждой смене,
                    и без даты четыре «0004» выглядят одинаково */}
                <div className="ol-day">
                  {new Date(r.openedAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                </div>
              </td>
              <td>
                <span className={`badge ${STATUS[r.status].cls}`}>
                  {STATUS[r.status].label}
                </span>
              </td>
              <td>{r.tableName ?? '—'}</td>
              <td>{r.waiter ?? '—'}</td>
              <td>{hhmm(r.openedAt)}</td>
              <td>{r.closedAt ? hhmm(r.closedAt) : <i className="ol-dim">Не закрыт</i>}</td>
              <td>
                {/* Способы построчно: гость платил картой и наличными —
                    владелец видит разбивку, не открывая заказ */}
                {r.payments.length === 0
                  ? <i className="ol-dim">Не оплачено</i>
                  : r.payments.map((p, i) => (
                      <div key={i} className="ol-pay">
                        <b>{METHOD_RU[p.method] ?? p.method}:</b> {formatMoney(p.amount)}
                      </div>
                    ))}
              </td>
              <td className="num"><b>{formatMoney(r.total)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>

      {openId && (
        <OrderDetail token={token} id={openId} onClose={() => setOpenId(null)} />
      )}

      {/* Лист журнала рисуем, только когда счёт закрыт: иначе при печати
          одного счёта из принтера выйдут оба листа — сначала журнал
          за весь день, потом нужная бумага */}
      {!openId && (
        <PrintSheet rows={data.rows} totals={data.totals} from={from} to={to} />
      )}
    </section>
  );
}

/**
 * Подробности заказа.
 *
 * Всё, что нужно для разбора: кто вёл, что заказали, какие скидки,
 * чем платили и когда. Владелец открывает это, когда гость звонит
 * и спорит о сумме — ответ должен найтись за десять секунд.
 */
function OrderDetail({ token, id, onClose }: {
  token: string;
  id: string;
  onClose: () => void;
}) {
  /*
   * Печать одного счёта.
   *
   * Прятать журнал за спиной правилами CSS оказалось ненадёжно:
   * лист журнала уже показан при печати, и он же выходил из принтера
   * вместо счёта. Поэтому рисуем ОТДЕЛЬНЫЙ печатный лист — тот же
   * приём, что у закрытия дня.
   */
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!printing) return;
    const done = () => setPrinting(false);
    window.addEventListener('afterprint', done);
    // Кадр на отрисовку листа: печатать пустое было бы обидно
    const t = setTimeout(() => window.print(), 60);
    return () => { window.removeEventListener('afterprint', done); clearTimeout(t); };
  }, [printing]);
  const q = useQuery({
    queryKey: ['order-detail', id],
    queryFn: async () => {
      const r = await fetch(`${API}/reports/orders-log/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return r.json() as Promise<{
        number: number; status: Row['status'];
        placeName: string; tableName: string | null;
        openedAt: string; closedAt: string | null;
        waiter: string; cashier: string; comment: string | null;
        subtotal: number; discount: number; service: number; total: number;
        items: { name: string; qty: number; unitPrice: number; sum: number;
          modifiers: { name: string }[] }[];
        payments: { method: string; amount: number; at: string }[];
      }>;
    },
  });

  const d = q.data;

  return (
    <div className="dialog" onClick={onClose}>
      <div className="dialog-card order-detail" onClick={(e) => e.stopPropagation()}>
        <header className="od-head">
          <h2>Счёт № {d ? String(d.number).padStart(4, '0') : '…'}</h2>
          <div className="od-actions">
            {/* Печать одного счёта на A4: гость просит документ,
                бухгалтер подшивает, спор разбирают через месяц.
                Лента из кассового принтера для этого не годится —
                она выцветает за полгода */}
            {d && (
              <button className="btn small" onClick={() => setPrinting(true)}>
                ⎙ Печать A4
              </button>
            )}
            <button className="od-x" onClick={onClose} aria-label="Закрыть">×</button>
          </div>
        </header>

        {q.isPending && <p className="hint">Загружаем…</p>}

        {d && (
          <>
            <div className="od-cards">
              <div className="od-card">
                <span>Статус</span>
                <b>{STATUS[d.status].label}</b>
              </div>
              <div className="od-card">
                <span>Точка продаж</span>
                <b>{d.placeName}</b>
              </div>
              <div className="od-card">
                <span>Стол</span>
                <b>{d.tableName ?? '—'}</b>
              </div>
              <div className="od-card">
                <span>Открыт</span>
                <b>{hhmm(d.openedAt)}</b>
              </div>
              <div className="od-card">
                <span>Закрыт</span>
                <b>{d.closedAt ? hhmm(d.closedAt) : '—'}</b>
              </div>
              <div className="od-card">
                <span>Итог</span>
                <b>{formatMoney(d.total)}</b>
              </div>
              <div className="od-card">
                <span>Официант</span>
                <b>{d.waiter}</b>
              </div>
              {d.cashier !== '—' && d.cashier !== d.waiter && (
                <div className="od-card">
                  <span>Принял оплату</span>
                  <b>{d.cashier}</b>
                </div>
              )}
            </div>

            {/* Комментарий официанта: «день рождения», «жалоба по подаче».
                При разборе спорной ситуации это первое, что ищут */}
            {d.comment && (
              <div className="od-comment">
                <b>Комментарий</b>
                <span>{d.comment}</span>
              </div>
            )}

            <h3 className="od-part">
              Позиции <span className="od-count">{d.items.length}</span>
            </h3>
            <table className="grid od-items">
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th className="num">Кол-во</th>
                  <th className="num">Цена</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {d.items.map((i, n) => (
                  <tr key={n}>
                    <td>
                      {i.name}
                      {i.modifiers.length > 0 && (
                        <div className="sub">{i.modifiers.map((m) => m.name).join(', ')}</div>
                      )}
                    </td>
                    <td className="num">{i.qty}</td>
                    <td className="num">{formatMoney(i.unitPrice)}</td>
                    <td className="num"><b>{formatMoney(i.sum)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="od-totals">
              <div className="od-trow">
                <span>Подытог</span><b>{formatMoney(d.subtotal)}</b>
              </div>
              {d.discount > 0 && (
                <div className="od-trow minus">
                  <span>Скидка</span><b>−{formatMoney(d.discount)}</b>
                </div>
              )}
              {d.service > 0 && (
                <div className="od-trow">
                  <span>Обслуживание</span><b>+{formatMoney(d.service)}</b>
                </div>
              )}
              <div className="od-trow total">
                <span>Итого</span><b>{formatMoney(d.total)}</b>
              </div>
            </div>

            {d.payments.length > 0 && (
              <>
                <h3 className="od-part">Оплата</h3>
                <table className="grid od-pays">
                  <thead>
                    <tr><th>Способ</th><th className="num">Сумма</th><th>Время</th></tr>
                  </thead>
                  <tbody>
                    {d.payments.map((p, n) => (
                      <tr key={n}>
                        <td>{METHOD_RU[p.method] ?? p.method}</td>
                        <td className="num"><b>{formatMoney(p.amount)}</b></td>
                        <td>{hhmm(p.at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {d.payments.length === 0 && d.status !== 'cancelled' && (
              <p className="hint">
                Заказ ещё не оплачен — деньги не получены.
              </p>
            )}

            {/* Печатный лист: отдельный от экранного, потому что
                на бумаге нужны реквизиты и подпись, а карточки
                и кнопки не нужны вовсе */}
            {printing && (
              <div className="print-sheet">
                <h1>Счёт № {String(d.number).padStart(4, '0')}</h1>
                <p className="ps-sub">
                  {d.placeName} · {STATUS[d.status].label}
                  {d.tableName ? ` · стол ${d.tableName}` : ''}
                </p>

                <table className="ps-table" style={{ width: '60%' }}>
                  <tbody>
                    <tr><td>Открыт</td><td className="r">{hhmm(d.openedAt)}</td></tr>
                    {d.closedAt && (
                      <tr><td>Закрыт</td><td className="r">{hhmm(d.closedAt)}</td></tr>
                    )}
                    <tr><td>Официант</td><td className="r">{d.waiter}</td></tr>
                    {d.cashier !== '—' && (
                      <tr><td>Принял оплату</td><td className="r">{d.cashier}</td></tr>
                    )}
                  </tbody>
                </table>

                {d.comment && <p className="ps-sub">Комментарий: {d.comment}</p>}

                <h2>Позиции</h2>
                <table className="ps-table">
                  <thead>
                    <tr>
                      <th>Позиция</th><th className="r">Кол-во</th>
                      <th className="r">Цена</th><th className="r">Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((i, n) => (
                      <tr key={n}>
                        <td>
                          {i.name}
                          {i.modifiers.length > 0 && ` (${i.modifiers.map((m) => m.name).join(', ')})`}
                        </td>
                        <td className="r">{i.qty}</td>
                        <td className="r">{formatMoney(i.unitPrice)}</td>
                        <td className="r">{formatMoney(i.sum)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <table className="ps-table ps-sums">
                  <tbody>
                    <tr><td>Подытог</td><td className="r">{formatMoney(d.subtotal)}</td></tr>
                    {d.discount > 0 && (
                      <tr><td>Скидка</td><td className="r">−{formatMoney(d.discount)}</td></tr>
                    )}
                    {d.service > 0 && (
                      <tr><td>Обслуживание</td><td className="r">+{formatMoney(d.service)}</td></tr>
                    )}
                    <tr className="ps-strong">
                      <td>Итого</td><td className="r">{formatMoney(d.total)}</td>
                    </tr>
                  </tbody>
                </table>

                {d.payments.length > 0 && (
                  <>
                    <h2>Оплата</h2>
                    <table className="ps-table" style={{ width: '60%' }}>
                      <tbody>
                        {d.payments.map((p2, n) => (
                          <tr key={n}>
                            <td>{METHOD_RU[p2.method] ?? p2.method}</td>
                            <td className="r">{formatMoney(p2.amount)}</td>
                            <td className="r">{hhmm(p2.at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                <p className="ps-sign">
                  Выдал: _____________________ &nbsp;&nbsp;
                  Получил: _____________________
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Печатный лист журнала — A4.
 *
 * Зачем его печатают: сдать день, показать бухгалтеру, подшить
 * в папку, разобраться со спором через неделю. Во всех случаях
 * бумага должна отвечать на вопросы без компьютера рядом.
 *
 * Поэтому здесь не только список, но и три свода: по способам оплаты
 * (сверить кассу), по официантам (посчитать процент) и по состояниям
 * (найти незакрытое). Каждый из них владелец иначе считал бы вручную.
 *
 * Печатаем чёрным по белому: цветной принтер в кафе редкость,
 * а серый бейдж на чёрно-белой печати превращается в мутное пятно.
 */
function PrintSheet({ rows, totals, from, to }: {
  rows: Row[];
  totals: { count: number; revenue: number; working: number };
  from: string;
  to: string;
}) {
  const now = new Date();

  // Свод по способам: с ним сверяют ящик и терминал в конце дня
  const byMethod = new Map<string, number>();
  for (const r of rows) {
    for (const p of r.payments) {
      byMethod.set(p.method, (byMethod.get(p.method) ?? 0) + p.amount);
    }
  }

  // Свод по официантам: по нему считают процент и спорят о столах
  const byWaiter = new Map<string, { count: number; sum: number }>();
  for (const r of rows) {
    if (r.status !== 'closed') continue;
    const key = r.waiter ?? '—';
    const cur = byWaiter.get(key) ?? { count: 0, sum: 0 };
    cur.count += 1;
    cur.sum += r.total;
    byWaiter.set(key, cur);
  }

  const openRows = rows.filter((r) => r.status === 'working' || r.status === 'awaiting');
  const dayAgo = Date.now() - 24 * 3600_000;

  return (
    <div className="print-sheet ol-print">
      <h1>Журнал заказов</h1>
      {/* Период в шапке обязателен: бумага уходит в папку, и через
          месяц без него не понять, за что эти цифры */}
      <p className="ps-period">
        {from === to
          ? new Date(`${from}T12:00`).toLocaleDateString('ru-RU',
              { day: 'numeric', month: 'long', year: 'numeric' })
          : `${new Date(`${from}T12:00`).toLocaleDateString('ru-RU')} — ${new Date(`${to}T12:00`).toLocaleDateString('ru-RU')}`}
      </p>
      <p className="ps-sub">
        Напечатано {now.toLocaleString('ru-RU', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })} · заказов {totals.count} · выручка {formatMoney(totals.revenue)}
      </p>

      {/* Незакрытые — первыми и отдельно: ради них журнал чаще всего
          и печатают, а в общем списке они теряются среди закрытых */}
      {openRows.length > 0 && (
        <>
          <h2>Незакрытые заказы · {openRows.length}</h2>
          <table className="ps-table">
            <thead>
              <tr>
                <th>Заказ</th><th>Стол</th><th>Официант</th>
                <th>Открыт</th><th className="r">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {openRows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {String(r.number).padStart(4, '0')}
                    {new Date(r.openedAt).getTime() < dayAgo && ' ⚠'}
                  </td>
                  <td>{r.tableName ?? '—'}</td>
                  <td>{r.waiter ?? '—'}</td>
                  <td>{hhmm(r.openedAt)}</td>
                  <td className="r">{formatMoney(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ps-note">
            Знаком ⚠ помечены столы, открытые больше суток назад.
          </p>
        </>
      )}

      <h2>Все заказы</h2>
      <table className="ps-table">
        <thead>
          <tr>
            <th>Заказ</th><th>Статус</th><th>Стол</th><th>Официант</th>
            <th>Открыт</th><th>Закрыт</th><th>Оплата</th><th className="r">Сумма</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{String(r.number).padStart(4, '0')}</td>
              <td>{STATUS[r.status].label}</td>
              <td>{r.tableName ?? '—'}</td>
              <td>{r.waiter ?? '—'}</td>
              <td>{hhmm(r.openedAt)}</td>
              <td>{r.closedAt ? hhmm(r.closedAt) : '—'}</td>
              <td>
                {r.payments.length === 0
                  ? '—'
                  : r.payments.map((p) => `${METHOD_RU[p.method] ?? p.method} ${formatMoney(p.amount)}`).join(', ')}
              </td>
              <td className="r">{formatMoney(r.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {byMethod.size > 0 && (
        <>
          <h2>По способам оплаты</h2>
          <table className="ps-table ps-narrow">
            <tbody>
              {[...byMethod.entries()].map(([m, sum]) => (
                <tr key={m}>
                  <td>{METHOD_RU[m] ?? m}</td>
                  <td className="r">{formatMoney(sum)}</td>
                </tr>
              ))}
              <tr className="ps-strong">
                <td>Итого получено</td>
                <td className="r">
                  {formatMoney([...byMethod.values()].reduce((a2, b2) => a2 + b2, 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {byWaiter.size > 0 && (
        <>
          <h2>По официантам</h2>
          <table className="ps-table ps-narrow">
            <thead>
              <tr><th>Сотрудник</th><th className="r">Чеков</th><th className="r">Сумма</th></tr>
            </thead>
            <tbody>
              {[...byWaiter.entries()]
                .sort((a2, b2) => b2[1].sum - a2[1].sum)
                .map(([name, v]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td className="r">{v.count}</td>
                    <td className="r">{formatMoney(v.sum)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}

      {/* Подписи: журнал сдают из рук в руки, и бумага без подписи
          в споре ничего не значит */}
      <div className="ps-signs">
        <p className="ps-sign">Смену сдал: _____________________ / _______________</p>
        <p className="ps-sign">Смену принял: ___________________ / _______________</p>
      </div>
    </div>
  );
}
